import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { inventoryItemAPI } from '../api/inventoryItemAPI';
import { reasonOptions, findReasonOption } from '../utils/disposeUtils';
import { formatErrorResponse } from '../utils/errorHandlers';
import { usePermissions } from '../contexts/PermissionContext';
import Select from 'react-select';

function BulkDisposeModal({ show, onHide, selectedItems, onSuccess }) {
    const { hasPermission } = usePermissions();
    const [reason, setReason] = useState(null);
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedItemDetails, setSelectedItemDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [itemQuantities, setItemQuantities] = useState({});
    const [activeItems, setActiveItems] = useState([]);
    
    // Define reason options - now imported from disposeUtils.js
    // const reasonOptions = [
    //     { value: 'quality', label: 'Quality' },
    //     { value: 'expiration', label: 'Expiration' },
    //     { value: 'short-dated', label: 'Short-Dated' },
    //     { value: 'damaged', label: 'Damaged' },
    //     { value: 'recall', label: 'Recall' },
    //     { value: 'depricated', label: 'Depricated' },
    //     { value: 'poor-sales', label: 'Poor Sales' },
    //     { value: 'other', label: 'Other' }
    // ];
    
    // Initialize active items and quantities when selectedItems change
    useEffect(() => {
        if (selectedItems && selectedItems.length > 0) {
            setActiveItems([...selectedItems]);
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);
        }
    }, [selectedItems]);
    
    // Fetch item details when the modal opens
    useEffect(() => {
        const fetchData = async () => {
            if (!show) return;
            
            try {
                setIsLoading(true);
                setSelectedItemDetails([]); // Clear existing data
                
                // Fetch item details
                if (selectedItems.length > 0) {
                    const itemIds = selectedItems.map(item => item.id).join(',');
                    const response = await inventoryItemAPI.getByIds(itemIds);
                    
                    // Extract items array from response
                    const items = response?.results || [];
                    
                    if (items.length > 0) {
                        setSelectedItemDetails(items);
                    } else {
                        setError('Failed to load item details - no data returned');
                        setSelectedItemDetails([]);
                    }
                }
            } catch (error) {
                setError(formatErrorResponse(error));
                setSelectedItemDetails([]);
            } finally {
                setIsLoading(false);
            }
        };
        
        if (show) {
            // Reset state when modal opens
            setReason(null);
            setComments('');
            setError('');
            setIsSubmitting(false);
            setActiveItems([...selectedItems]); // Reset active items to original selection
            
            // Reset item quantities to empty values
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);
            
            fetchData();
        }
    }, [show, selectedItems]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!reason) {
            setError('Please select a reason for disposal');
            return;
        }
        
        // Create items to dispose with quantities from local state
        const itemsToDispose = activeItems.map(item => ({
            id: item.id,
            quantity: parseInt(itemQuantities[item.id]) || 0
        }));
        
        // Check if quantities are valid
        const hasInvalidQuantity = itemsToDispose.some(item => 
            !item.quantity || 
            isNaN(item.quantity) || 
            item.quantity <= 0
        );
        
        if (hasInvalidQuantity) {
            setError('Quantity must be a positive number for all items');
            return;
        }
        
        try {
            setIsSubmitting(true);
            await inventoryItemAPI.disposeBulk(itemsToDispose, reason.value, comments);
            setIsSubmitting(false);
            onSuccess(); // Called after successful disposal
            onHide();
        } catch (error) {
            console.error('Error disposing items:', error);
            setError(formatErrorResponse(error));
            setIsSubmitting(false);
        }
    };
    
    const getTotalItems = () => {
        return Object.values(itemQuantities).reduce((sum, quantity) => sum + (parseInt(quantity) || 0), 0);
    };

    const handleQuantityChange = (id, value) => {
        // Update quantities locally
        setItemQuantities(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleRemoveItem = (itemId) => {
        // Don't allow removing the last item
        if (activeItems.length <= 1) {
            return;
        }
        
        // Remove item from activeItems
        setActiveItems(prev => prev.filter(item => item.id !== itemId));
        
        // Remove quantity entry for this item
        setItemQuantities(prev => {
            const newQuantities = { ...prev };
            delete newQuantities[itemId];
            return newQuantities;
        });
    };
    
    // Find item details safely
    const findItemDetail = React.useCallback((itemId) => {
        if (!Array.isArray(selectedItemDetails)) {
            return null;
        }

        const itemFromDetails = selectedItemDetails.find(detail => detail._id === itemId);
        return itemFromDetails || null;
    }, [selectedItemDetails]);

    // Check permission
    if (show && !hasPermission('inventory.dispose')) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Permission Denied</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        You don&apos;t have permission to dispose inventory items.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal 
            show={show} 
            onHide={() => {
                setItemQuantities({});
                setSelectedItemDetails([]);
                setError('');
                onHide();
            }} 
            size="lg"
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>Dispose {activeItems.length} Item{activeItems.length !== 1 ? 's' : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading item details...</span>
                                </Spinner>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>GTIN</th>
                                            <th>Description</th>
                                            <th>Location</th>
                                            <th>Available</th>
                                            <th>Quantity to Dispose</th>
                                            <th style={{width: '50px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeItems.map(item => {
                                            const itemDetail = findItemDetail(item.id);
                                            return (
                                                <tr key={item.id}>
                                                    <td>{itemDetail?.stock_item?.gtin || 
                                                        <span className="text-danger">Data missing</span>}</td>
                                                    <td>{itemDetail?.stock_item?.description || 
                                                        <span className="text-danger">Data missing</span>}</td>
                                                    <td>{itemDetail?.location_name || 
                                                        <span className="text-danger">Data missing</span>}</td>
                                                    <td>{itemDetail?.quantity || 
                                                        <span className="text-danger">Data missing</span>}</td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            max={itemDetail?.quantity || 1}
                                                            value={itemQuantities[item.id] || ''}
                                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                            required
                                                            size="sm"
                                                            disabled={!itemDetail}
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                        {!itemDetail && (
                                                            <small className="text-muted">Adjust filters to see this item</small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            disabled={activeItems.length <= 1}
                                                            title={activeItems.length <= 1 ? "Cannot remove the last item" : "Remove item"}
                                                        >
                                                            ×
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5" className="text-end"><strong>Total Items:</strong></td>
                                            <td><strong>{getTotalItems()}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    <h5>Disposal Details</h5>
                    <Form.Group className="mb-3">
                        <Form.Label>Reason*</Form.Label>
                        <Select
                            value={reason}
                            onChange={setReason}
                            options={reasonOptions}
                            isSearchable
                            placeholder="Select reason"
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Comments</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any comments about this disposal..."
                            autoComplete="off"
                            spellCheck="true"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    {error && <Alert variant="danger" className="mb-3 w-100">{error}</Alert>}
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="danger" disabled={isSubmitting || !reason}>
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Disposing...
                            </>
                        ) : (
                            'Dispose Items'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default BulkDisposeModal; 